import React, { useEffect, useState } from "react";
import { Button, Empty, Image, Space, Typography, message } from "antd";
import {
  DownloadOutlined,
  ExportOutlined,
  FileUnknownOutlined,
} from "@ant-design/icons";

import { downloadFile, getFileKind } from "./fileTypes";

const { Text } = Typography;

const DEFAULT_HEIGHT = "70vh";

/**
 * Renders a single file inline: PDFs in the browser's built-in viewer, images
 * in an antd preview (click to zoom/rotate without leaving the page).
 */
const FileViewer = ({
  url,
  kind: kindOverride,
  title = "Document",
  downloadLabel,
  downloadSuffix = "",
  fileName,
  height = DEFAULT_HEIGHT,
  showActions = true,
}) => {
  const [unpreviewable, setUnpreviewable] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setUnpreviewable(false);
  }, [url]);

  const containerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: height,
    background: "#fafafa",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    padding: 12,
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile(
        url,
        fileName || { label: downloadLabel || title, suffix: downloadSuffix }
      );
    } catch (error) {
      message.error("Could not download this file");
    } finally {
      setDownloading(false);
    }
  };

  const renderContent = () => {
    if (!url) {
      return (
        <div style={containerStyle}>
          <Empty description="No file attached" />
        </div>
      );
    }

    const kind = kindOverride || getFileKind(url);

    if (unpreviewable || kind === "other") {
      return (
        <div style={containerStyle}>
          <Empty
            image={
              <FileUnknownOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />
            }
            description={
              <Space direction="vertical" size={2}>
                <Text strong>This file cannot be previewed here</Text>
                <Text type="secondary">
                  Download it to open in another application.
                </Text>
              </Space>
            }
          />
        </div>
      );
    }

    if (kind === "pdf") {
      // <object> rather than <iframe>: a browser without a built-in PDF viewer
      // renders the fallback below instead of silently starting a download,
      // which is the behaviour this viewer exists to avoid.
      return (
        <object
          data={url}
          type="application/pdf"
          aria-label={title}
          style={{
            width: "100%",
            height,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}
        >
          <div style={containerStyle}>
            <Empty
              image={
                <FileUnknownOutlined
                  style={{ fontSize: 48, color: "#bfbfbf" }}
                />
              }
              description={
                <Space direction="vertical" size={2}>
                  <Text strong>This browser cannot display PDFs inline</Text>
                  <Text type="secondary">
                    Use Download or Open in new tab instead.
                  </Text>
                </Space>
              }
            />
          </div>
        </object>
      );
    }

    return (
      <div style={containerStyle}>
        <Image
          src={url}
          alt={title}
          style={{ maxHeight: height, objectFit: "contain" }}
          onError={() => setUnpreviewable(true)}
        />
      </div>
    );
  };

  return (
    <>
      {renderContent()}
      {showActions && url ? (
        <Space style={{ marginTop: 12 }}>
          <Button
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
          >
            Download
          </Button>
          <Button
            type="link"
            icon={<ExportOutlined />}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in new tab
          </Button>
        </Space>
      ) : null}
    </>
  );
};

export default FileViewer;
